'use client';

import React, { FC } from 'react';
import { useParams, usePathname } from 'next/navigation';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import { mapProduct } from 'helpers/algolia/map-product';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';
import ProductTile from '../product-tile';

export interface RelatedProductsSliderProps {
  title?: string;
  /** Server-fetched Algolia hits (backend action → Redis). Already shuffled and capped. */
  hits: any[];
}

/**
 * The carousel for `commercetools/ui/products/slider` — PDP "Related products", the cross-sell row on
 * collection pages, and the product row on /our-company.
 *
 * A pure view: its products are fetched and ordered on the SERVER (see the tastic and
 * helpers/algolia/fetch-related-products), so nothing here queries Algolia and the tiles are in the
 * SSR HTML. It was previously an InstantSearch-connected component, which meant `<Index>` mounted in
 * an effect and a crawler saw an empty section.
 */
const RelatedProductsSlider: FC<RelatedProductsSliderProps> = ({ title, hits }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const { locale } = useParams();
  const currentURL = usePathname();

  const isProductDetailPage = isPdpUrl(currentURL);

  return (
    <div
      className={`pt-22 md:pt-22 pb-48 md:pb-48 lg:py-48 xl:py-70 2xl:py-70 3xl:py-103  ${
        isProductDetailPage
          ? 'lg:grid lg:grid-cols-12 pr-0  md:pb-1  bg-[#EDEDED] w-screen relative left-full -translate-x-full'
          : 'bg-[#E5E9EB] px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80'
      }`}
    >
      <div
        id="related-products"
        className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4 pt-26 pl-24 md:pl-28 lg:pl-30 xl:pl-0 2xl:pl-0 xl:py-60' : ''}
      >
        <div>
          <Typography
            id="related-products-title"
            className="pb-32 text-32 font-medium text-primary-black xl:text-36 2xl:text-48"
            as="h3"
          >
            {title}
          </Typography>
        </div>
        <div>
          <div className="product-slider-container relative w-full">
            <Slider
              className="mb-48"
              slidesPerView={2.1}
              slidesPerGroup={1}
              dots={false}
              autoplay={false}
              arrows={isTabletSize || isDesktopSize || isMiniTabletSize}
              innerArrows
              solidArrows
              allowArrowsOnTouchDevice
              nextButtonStyles={{ top: '100%', left: 50 }}
              prevButtonStyles={{ top: '100%', left: 0 }}
              allowTouchMove
              spaceBetween={8}
              breakpoints={{
                [tablet]: {
                  slidesPerView: isProductDetailPage ? 3.25 : 4,
                  slidesPerGroup: isProductDetailPage ? 3 : 4,
                  spaceBetween: 16,
                },
                [smallMobile]: {
                  slidesPerView: 1.2,
                  slidesPerGroup: 1,
                  spaceBetween: 16,
                },
                [mobile]: {
                  slidesPerView: 2.1,
                  slidesPerGroup: 2,
                  spaceBetween: 16,
                },
              }}
            >
              {hits.map((hit: any, index: number) => (
                <div key={hit.objectID ?? hit.key ?? index} className="mb-48">
                  <ProductTile product={mapProduct(hit, locale as string)} layout="grid" />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedProductsSlider;
